import React from 'react';
import { Modal } from '@mantine/core';
import { Button } from '../../../components/Button';
import { RESTIGO_KIND_LABELS, RESTIGO_STATUS_LABELS } from '../monitoring-page.constants';
import { formatDateTime } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type RestigoCallLogDetailModalProps = Pick<
  UseMonitoringPage,
  'selectedRestigo' | 'closeRestigoDetail' | 'resendRestigo' | 'isResendingRestigo'
>;

const prettyJson = (value: string | null): string => {
  if (!value) {
    return '—';
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

export const RestigoCallLogDetailModal: React.FC<RestigoCallLogDetailModalProps> = ({
  selectedRestigo,
  closeRestigoDetail,
  resendRestigo,
  isResendingRestigo,
}) => {
  const row = selectedRestigo;

  const fields: { label: string; value: string }[] = row
    ? [
        { label: 'תאריך ושעה', value: formatDateTime(row.createdAt) },
        { label: 'עסק', value: row.companyName ?? row.tenantId ?? '-' },
        { label: 'דוח', value: RESTIGO_KIND_LABELS[row.kind] ?? row.kind },
        { label: 'סטטוס', value: RESTIGO_STATUS_LABELS[row.status] ?? row.status },
        { label: 'קוד HTTP', value: row.httpStatus != null ? String(row.httpStatus) : '-' },
        { label: 'זמן תגובה', value: row.latencyMs != null ? `${row.latencyMs} ms` : '-' },
        { label: 'ניסיון', value: String(row.attempt) },
        { label: 'מספר הפעלה', value: row.zNumber ?? (row.sessionId != null ? String(row.sessionId) : '-') },
        { label: 'יעד', value: row.endpoint },
      ]
    : [];

  return (
    <Modal
      opened={row != null}
      onClose={closeRestigoDetail}
      title={row ? `${RESTIGO_KIND_LABELS[row.kind] ?? row.kind} · ${RESTIGO_STATUS_LABELS[row.status] ?? row.status}` : ''}
      centered
      size="xl"
      closeButtonProps={{ 'data-testid': 'monitoring-restigo-detail-close' } as Record<string, string>}
    >
      {row && (
        <div className={classes.errorDetail} data-testid="monitoring-restigo-detail">
          <div className={classes.detailsSection}>
            {fields.map((field) => (
              <div key={field.label} className={classes.detailField}>
                <span className={classes.detailLabel}>{field.label}</span>
                <span className={classes.detailValue}>{field.value}</span>
              </div>
            ))}
          </div>

          {row.status !== 'success' && row.sessionId != null && (
            <div className={classes.filtersActions}>
              <Button
                variant="save"
                onClick={() => resendRestigo(row)}
                disabled={isResendingRestigo}
                dataTestId="monitoring-restigo-detail-resend"
              >
                {'שלח שוב לרסטיגו'}
              </Button>
            </div>
          )}

          {row.errorMessage && (
            <>
              <div className={classes.sectionTitle}>{'שגיאה'}</div>
              <pre className={classes.errorTrace}>{row.errorMessage}</pre>
            </>
          )}

          <div className={classes.sectionTitle}>{'בקשה'}</div>
          <pre className={classes.errorTrace}>{prettyJson(row.requestPayload)}</pre>

          <div className={classes.sectionTitle}>{'תגובה'}</div>
          <pre className={classes.errorTrace}>{prettyJson(row.responseBody)}</pre>
        </div>
      )}
    </Modal>
  );
};
